import { dialog } from "../../../nq-component";
import BaseListPresenter from "../../../base/BaseListPresenter";
import { parseGross } from "../../receipts/form/utils/receiptValidations";
import changes from "../../../changes";
import camelToTitleCase from "../../../camelToTitleCase";

class SalesListPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    getCurrentUserUseCase,
    updateObjectUseCase,
  ) {
    super(
      view,
      findObjectUseCase,
      countObjectUseCase,
      deleteObjectUseCase,
      upsertUseCase,
      updateObjectUseCase,
    );
    this.getCurrentUserUseCase = getCurrentUserUseCase;
    this.updateObjectUseCase = updateObjectUseCase;
    this.objects = [];
  }

  async componentDidMount() {
    const clientId = this.view.getClientId();
    if (clientId) {
      await this.getUserClient();
    }

    this.init();

    return this.getObjects();
  }

  init() {
    const { userObject } = this.view.state;
    const user = this.view.getCurrentUser();
    const clientId = this.view.getClientId();
    const month = this.view.getParamsMonth();
    const year = this.view.getParamsYear();

    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 1);

    this.limit = 20;
    this.where = {
      date: { $gte: start, $lt: end },
      creator: { id: userObject?.id || user?.id },
    };
    this.search = {};
    this.filter = {};
    this.include = ["client", "bank"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { created: -1 };
    this.progress = true;
    this.reset();
  }

  async onSubmitModal(collection, object) {
    const updatedObject = { ...object };
    try {
      this.view.showProgress();

      await this.upsertUseCase.execute(collection, updatedObject);

      this.view.hideProgress();
      this.view.showSuccess("Data Added Successfully!");
    } catch (error) {
      this.view.showError(error);
    }
  }

  getMonths() {
    const year = new Date().getFullYear();
    const months = Array.from({ length: 12 }, (_, i) => {
      return new Intl.DateTimeFormat("en-US", { month: "long" }).format(
        new Date(year, i, 1),
      );
    });

    return months;
  }

  async onSubmit(updatedObject, originalObject) {
    const collection = this.view.getCollectionName();
    const route = this.view.getBaseRoute?.();
    const isAdmin = this.view.isAdmin();
    const clientId = this.view.getClientId();
    const hasChanges =
      originalObject != null &&
      Object.keys(changes(originalObject, updatedObject)).length > 0;

    const bankId =
      typeof updatedObject.bank === "object"
        ? updatedObject.bank?.id
        : updatedObject.bank;

    const grossAmount = parseGross(updatedObject.gross);
    const currentBalance = updatedObject.bank?.balance || 0;
    const updatedBalance = currentBalance + grossAmount;

    const updatedSales = {
      ...updatedObject,
      status: isAdmin
        ? updatedObject?.status
        : updatedObject?.isVatable
          ? "pending"
          : "non-vat",
      bank: { id: bankId },
    };

    try {
      this.view.showProgress();
      dialog.close();
      await this.upsertUseCase.execute(collection, updatedSales);

      await this.upsertUseCase.execute("banks", {
        id: bankId,
        balance: updatedBalance,
      });

      if (hasChanges) {
        const changedFields = changes(originalObject, updatedObject);
        const changeSummary = Object.entries(changedFields)
          .map(([key, value]) => {
            const display =
              value != null &&
              typeof value === "object" &&
              !(value instanceof Date)
                ? (value?.name ?? value?.id ?? "[object]")
                : String(value);
            return `${camelToTitleCase(key)}: ${display}`;
          })
          .join(", ");

        if (isAdmin) {
          await this.createNotification({
            title: "Sales updated",
            description: `A sale record was updated by accountant: ${changeSummary}`,
            route: route,
            refId: clientId,
            client: { id: clientId },
          });
        }
      }

      this.getObjects();
      this.view.hideProgress();
      this.view.showSuccess("Object Updated Successfully!");
    } catch (error) {
      this.view.hideProgress();
      this.view.showError(error);
    }
  }

  async salesAudited(data) {
    const collection = this.view.getCollectionName();
    const isMultiple = Array.isArray(data);
    const objectsToAudit = isMultiple ? data : [data];
    const isAdmin = this.view.isAdmin();
    const route = this.view.getBaseRoute?.();
    const clientId = this.view.getClientId();

    const { objects } = this.view.state;
    const idsToAudit = objectsToAudit.map((o) => o.id).filter(Boolean);

    if (idsToAudit.length === 0) return;

    try {
      this.view.showProgress();

      await Promise.all(
        idsToAudit.map((id) =>
          this.updateObjectUseCase.execute(collection, {
            id,
            status: "audited",
          }),
        ),
      );

      const idSet = new Set(idsToAudit);
      const updatedObjects = objects.map((object) =>
        idSet.has(object.id) ? { ...object, status: "audited" } : object,
      );
      this.objects = updatedObjects;

      this.view.setState((prevState) => ({
        ...prevState,
        objects: [...updatedObjects],
        selected: [],
      }));

      this.view.hideProgress();

      const successMessage = isMultiple
        ? `${idsToAudit.length} Sales Audited Successfully!`
        : "Sales Audited Successfully!";

      if (isAdmin) {
        await this.createNotification({
          title: "Sales audited",
          description: isMultiple
            ? `${idsToAudit.length} sale(s) were audited by admin`
            : "Sales were audited by admin",
          route: route,
          refId: clientId,
          client: { id: clientId },
        });
      }

      this.view.showSuccess(successMessage);
    } catch (error) {
      this.view.hideProgress();
      this.view.showError(error);
    }
  }

  async getUserClient() {
    const collection = "users";
    const clientId = this.view.getClientId();
    const query = {
      where: {
        client: {
          id: clientId,
        },
      },
      include: ["client"],
    };
    console.warn("query", query);
    try {
      this.showProgress();

      const user = await this.findObjectUseCase.execute(collection, query);
      await new Promise((resolve) => {
        this.view.setState(
          { userObject: user[0] ? { ...user[0] } : {} },
          resolve,
        );
      });
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      console.error("error", error);
      this.view.showError(error);
    }
  }

  onSelectAll(checked) {
    const objects = this.objects.filter(
      (object) => object.status !== "audited",
    );
    if (checked) {
      this.view.setSelected([...objects]);
    } else {
      this.view.setSelected([]);
    }
  }
}

export default SalesListPagePresenter;
